/**
 * 账户与挂账枚举 —— `accounts.type` / `internal.category` / `accounts.contra_kind` /
 * `accounts.method` / `accounts.subtype` / `insurance_policies.status`
 *
 * 依据：规格 §22.6（权威枚举清单）· 架构 §4.2 / **§4.3** · `DR-021` · `DR-022` ·
 * **`DR-044`**（贷款参数载体：`method` / `subtype`）· `DR-016` §6.9（保单状态）
 */

// ─────────────────────────── 账户 8 类 ───────────────────────────

/**
 * 账户类型（`accounts.type`）—— 🔴 **权威 8 类**（规格 §22.6 · `DR-021` 派生）。
 *
 * ⚠️ v1 是 **7 类**；v2 新增 **`Contra`（借贷账户化）** ⇒ **8 类**。
 *
 * | 值 | 说明 |
 * |---|---|
 * | `cash` / `debit` / `ewallet` | 货币资金 |
 * | `credit` | 信用卡（余额 < 0 计入负债方） |
 * | `loan` | 贷款（余额计入负债方） |
 * | `investment` | 投资（P1 持仓明细；P0 仅表结构） |
 * | `internal` | 内部挂账（13 品类，见 `InternalCategory`） |
 * | `contra` | 🔴 借贷账户化（一条关系 / 一个事项 = 一个账户，见 `ContraKind`） |
 */
export const AccountType = {
  Cash: 'cash',
  Debit: 'debit',
  Ewallet: 'ewallet',
  Credit: 'credit',
  Loan: 'loan',
  Investment: 'investment',
  Internal: 'internal',
  Contra: 'contra',
} as const;
export type AccountType = (typeof AccountType)[keyof typeof AccountType];

// ─────────────────────── `internal` 13 品类 ───────────────────────

/**
 * `internal` 账户品类（`accounts.category`）—— 🔴 **权威 13 个**（架构 §4.3 · `DR-021`）。
 *
 * 🔴 **形态 = 扁平枚举，层级由取值本身表达**（不新增列、不做子账户树）——
 * 架构 §4.3 明确「避开同一事实两份载体」。
 *
 * ⚠️ 下面用注释分组只是**给人看的**；**代码里禁止按这 3 组做分支**（分组不是口径，
 * 口径在 §5.4 的净资产判定表里逐值给出）。
 */
export const InternalCategory = {
  // ── 非社保（4）──
  /** 待缴：可负 + 有周期规则 */
  Payable: 'payable',
  /** 预付卡：仅正，**无周期规则** */
  Prepaid: 'prepaid',
  /** 待摊：仅正 + 有周期规则 */
  Amortize: 'amortize',
  /** 保费挂账：🔴 **唯一入净资产表的例外**（架构 §5.4） */
  InsuranceSav: 'insurance_sav',

  // ── 社保 · 社保侧（6）—— 可与社保 App 对账 ──
  SocialPension: 'social_pension',
  SocialPensionSup: 'social_pension_sup',
  /** 社保侧医保（1 个）—— 🔴 `DR-021` v1.3 裁定**计资产** */
  SocialMedical: 'social_medical',
  SocialUnemploy: 'social_unemploy',
  SocialFund: 'social_fund',
  SocialFundSup: 'social_fund_sup',

  // ── 社保 · 虚拟侧（3）—— 电子虚拟户 ──
  /** 医保当年户 */
  SocialMedicalCur: 'social_medical_cur',
  /** 医保历年户 */
  SocialMedicalHist: 'social_medical_hist',
  /** 公积金虚拟户 */
  SocialFundVirtual: 'social_fund_virtual',
} as const;
export type InternalCategory = (typeof InternalCategory)[keyof typeof InternalCategory];

// ─────────────────── `contra` 事项类型 5 类 ───────────────────

/**
 * 借贷账户事项类型（`accounts.kind` when `type = 'contra'`）—— 5 类（`DR-022`）。
 *
 * 🔴 **账户名承载"事项"**（不新增 `purpose` 字段、不做子账户树）；
 * 🔴 余额**由腿派生、绝不落库**；**本金往来一律不计收支**。
 *
 * ⚠️ `loan` 另有 `subtype`（房贷 / 车贷 / 消费贷…）—— **取值由 `DR-044`（2026-10-16）细化落地**，
 * 见 {@link LoanSubtype}；本枚举**只承载"是不是贷款"**（`AC-P1.15`：新增枚举须同时选载体）。
 */
export const ContraKind = {
  /** 应收（借出的钱） */
  Receivable: 'receivable',
  /** 应付（欠的钱） */
  Payable: 'payable',
  /** 垫付（代他人垫的钱） */
  Advance: 'advance',
  /** 报销（公司与保险共用，`DR-022` Q4 泛化） */
  Reimburse: 'reimburse',
  /** 贷款（带 `subtype`） */
  Loan: 'loan',
} as const;
export type ContraKind = (typeof ContraKind)[keyof typeof ContraKind];

// ─────────────────── `loan` 的两个子枚举（`DR-044`） ───────────────────

/**
 * 还款方式（`accounts.method` when `type='contra'` 且 `contraKind='loan'`）
 * —— **2 值**（`DR-044` D4，2026-10-16 拍板）。
 *
 * 依据：`借贷域口径对齐单` §2.1「贷款专有：利率 / 期数 / 起息日 / **还款方式**」·
 * `F域PRD` §4.5.1（`loan` 计划表按本金 / 利率 / 期数**生成每期本金 + 利息拆分**）·
 * `Q域PRD` §5（「贷款再填利率期数」）。
 *
 * | 值 | 含义 | 每期特征 |
 * |---|---|---|
 * | `equal_installment` | **等额本息** | 每期**还款额相同**，利息前高后低 |
 * | `equal_principal` | **等额本金** | 每期**本金相同**，还款额逐期递减 |
 *
 * ## 🔴 三条纪律
 *
 * 1. **v1 存的是 `equal-installment`（连字符）** —— v2 枚举惯例是 **snake_case**
 *    ⇒ 迁移回填**必须显式映射**（`tools/migrate-from-v1.ts` 的 `--backfill-m37`），
 *    不得"照着 v1 抄一遍"（否则库里会出现一个枚举里没有的值）。
 * 2. **只放 2 值**：这是房贷 / 车贷覆盖到的全部；「气球贷 / 先息后本」等
 *    **等真实需求出现再加**（`AC-P1.15`：新增枚举须同时选载体，**不囤规则**）。
 * 3. 🔴 **本枚举是"参数"不是"算法"** —— 怎么按它算出每期金额属 **F 域**（{@link ScheduleKind} 的
 *    `loan` 生成器），本枚举**不承载任何计算规则**（`DR-044` D7 / D9：不做利息计息引擎）。
 */
export const RepaymentMethod = {
  /** 等额本息（每期还款额相同） */
  EqualInstallment: 'equal_installment',
  /** 等额本金（每期本金相同） */
  EqualPrincipal: 'equal_principal',
} as const;
export type RepaymentMethod = (typeof RepaymentMethod)[keyof typeof RepaymentMethod];

/** 全部还款方式（顺序 = 由常用到少用，用于表单与枚举审计） */
export const REPAYMENT_METHODS: readonly RepaymentMethod[] = Object.values(RepaymentMethod);

/**
 * 贷款子类型（`accounts.subtype` when `type='contra'` 且 `contraKind='loan'`）
 * —— **4 值**（`DR-044` D5，2026-10-16 拍板）。
 *
 * 依据：`借贷域口径对齐单` §2.1 line 63「贷款再带 `subtype`：**房贷 / 车贷 / 网贷 / 其他**」；
 * `ContraKind` 的 docstring 原本就写「子类型具体取值**归 Q 域细化**」⇒ `DR-044` 即那次细化。
 *
 * | 值 | 含义 |
 * |---|---|
 * | `mortgage` | 房贷 |
 * | `auto` | 车贷 |
 * | `consumer` | 消费贷 / 网贷 |
 * | `other` | 其他 |
 *
 * ⚠️ **与 `ContraKind` 正交**：`subtype` 只在 `contraKind='loan'` 时有值；
 * 其余 4 类 `contraKind` 一律 `NULL`（`DR-044` D1：列由 `type`/`contraKind` 解释语义）。
 *
 * ⚠️ v1 生产库只有 `mortgage` 一个值（3 行），其余 4 行为 `NULL` ⇒ 回填**无损**且不猜。
 */
export const LoanSubtype = {
  /** 房贷 */
  Mortgage: 'mortgage',
  /** 车贷 */
  Auto: 'auto',
  /** 消费贷 / 网贷 */
  Consumer: 'consumer',
  /** 其他 */
  Other: 'other',
} as const;
export type LoanSubtype = (typeof LoanSubtype)[keyof typeof LoanSubtype];

/** 全部贷款子类型（顺序 = 由常用到少用） */
export const LOAN_SUBTYPES: readonly LoanSubtype[] = Object.values(LoanSubtype);

// ─────────────────── 保单承保状态（6 态 · B5） ───────────────────

/**
 * 保单承保状态（`insurance_policies.status`）—— **6 态**（`DR-016` §6.9）。
 *
 * 起因：`DR-016` §6.9 原文「现 `active` / `paid_up` / `lapsed` → **补 `surrendered`（已退保）/
 * `matured`（已满期）/ `claimed`（已理赔）**，并与现金价值归零联动」。
 * 🔴 本枚举落地该条 —— 在此之前共享层**没有**保单状态枚举，服务端只能存裸字符串。
 *
 * 🔴 **与净资产的关系（口径，不是实现细节）**：只有 {@link PolicyStatus.Active} 与
 * {@link PolicyStatus.PaidUp} 的**现金价值计入净资产**（B 域 PRD §5.5.5）——
 * 判定唯一实现在 {@link cashValueCountsInNetWorth}（`nucleus/insurance.ts`），
 * **不得由各调用方各写一遍 `status === 'active' || ...`**。
 *
 * | 状态 | 含义 | 现金价值入净资产 | 备注 |
 * |---|---|---|:--:|---|
 * | `active` | 保障中 | ✅ | — |
 * | `paid_up` | 已缴清（保障继续） | ✅ | 仍是有价值的资产 |
 * | `lapsed` | 失效（欠缴） | ❌ | 已无有效保障；价值不得虚增净资产 |
 * | `claimed` | 已理赔 | ❌ | 已给付结算，价值不应再计入 |
 * | `surrendered` | 已退保 | ❌ | `DR-016` §6.9：**现金价值归零** |
 * | `matured` | 已满期 | ❌ | 同上；给付走「转账 + 差额不计收支」 |
 *
 * ⚠️ **`claimed` 的取舍**：B 域 PRD §5.5.5 只写了"限 `active`/`paid_up`"，未逐一列举后四态。
 * 本枚举按**同一原则**处理 = **"还有价值的存量才算资产"**：`lapsed` / `claimed` /
 * `surrendered` / `matured` 四态**都不计**。若产品侧另有定义（如理赔后仍保留现金价值），
 * **改 {@link cashValueCountsInNetWorth} 一处**即可。
 */
export const PolicyStatus = {
  /** 保障中 */
  Active: 'active',
  /** 已缴清（保障继续） */
  PaidUp: 'paid_up',
  /** 失效（欠缴） */
  Lapsed: 'lapsed',
  /** 已理赔 */
  Claimed: 'claimed',
  /** 已退保（现金价值归零） */
  Surrendered: 'surrendered',
  /** 已满期（现金价值归零） */
  Matured: 'matured',
} as const;
export type PolicyStatus = (typeof PolicyStatus)[keyof typeof PolicyStatus];

/** 全部承保状态（**顺序 = 生命周期**，用于表单与枚举审计） */
export const POLICY_STATUSES: readonly PolicyStatus[] = Object.values(PolicyStatus);

// ═══════════════════ `contra` 结算动作（`DR-047` · 批 82） ═══════════════════

/**
 * 🔴 **`contra` 结算动作的三类取值** —— `contra_settlements.type` 的权威枚举。
 *
 * | 值 | 中文 | 方向 | 收支 |
 * |---|---|---|---|
 * | `collect` | 收回（应收） | 钱回到手上 ⇒ `contra` 余额**向零** | 不计 |
 * | `repay` | 还款（应付 / 贷款） | 钱付出去 ⇒ `contra` 余额**向零** | 不计 |
 * | `waive` | 减免 / 核销 | 余额直接归零（无对手方账户） | 不计 |
 *
 * ## 为什么现在才有这个枚举
 *
 * 架构 §4.2 只给了中文（「还款 / 收回 / 减免」），而 `DR-044` **D6** 才把英文标识定死
 * （`repay` / `collect` / `waive`）—— 但那一批只落参数列，没动结算行 ⇒ 本枚举到批 `82`
 * 才随结算动作一起落地。⚠️ 批 `78` 迁移时**临时统一写 `'repay'`**，批 `79` 实测
 * 那 2 行确实都是应付的还款（车贷分期）⇒ **零订正**，与本节取值一致。
 */
export const SettlementType = {
  /** 收回（应收方向的结算） */
  Collect: 'collect',
  /** 还款（应付 / 贷款方向的结算） */
  Repay: 'repay',
  /** 减免 / 核销（余额直接归零，无对手方账户） */
  Waive: 'waive',
} as const;
export type SettlementType = (typeof SettlementType)[keyof typeof SettlementType];

/** 全部结算动作（**顺序 = 端侧按钮顺序**：先收回、再还款、最后核销） */
export const SETTLEMENT_TYPES: readonly SettlementType[] = Object.values(SettlementType);

/** 结算动作是否**需要一个对手方账户**（收/付款账户）—— `waive` 不需要（`DR-047` D3） */
export function settlementNeedsCounterAccount(type: SettlementType): boolean {
  return type !== SettlementType.Waive;
}

/** 结算动作的中文名（**服务端下发、端侧不各写一份** —— `DR-047` D6） */
export const SETTLEMENT_TYPE_LABEL: Readonly<Record<SettlementType, string>> = {
  [SettlementType.Collect]: '收回',
  [SettlementType.Repay]: '还款',
  [SettlementType.Waive]: '核销',
};
